import { clearServiceWorkerAndCaches } from './clearServiceWorkerAndCaches';

export const DEPLOYED_VERSION_URL = '/version.json';
export const VERSION_RECOVER_ATTEMPT_STORAGE_KEY = 'guarnic:version-recover-attempt';
export const VERSION_RECOVER_ATTEMPT_AT_STORAGE_KEY = 'guarnic:version-recover-attempt-at';

/** Allow another clear+reload for the same remote version after this cooldown. */
export const VERSION_RECOVER_RETRY_MS = 30 * 60 * 1000;

type DeployedVersionPayload = {
  version?: unknown;
};

type RecoverIfDeployedVersionMismatchOptions = {
  localVersion?: string;
  versionUrl?: string;
  fetchImpl?: typeof fetch;
  clearCaches?: () => Promise<void>;
  reload?: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  now?: number;
  retryAfterMs?: number;
};

const readDeployedVersion = async (
  versionUrl: string,
  fetchImpl: typeof fetch,
): Promise<string | null> => {
  const response = await fetchImpl(versionUrl, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as DeployedVersionPayload;
  return typeof payload?.version === 'string' && payload.version.length > 0
    ? payload.version
    : null;
};

const clearRecoverAttempt = (storage: Pick<Storage, 'removeItem'>): void => {
  storage.removeItem(VERSION_RECOVER_ATTEMPT_STORAGE_KEY);
  storage.removeItem(VERSION_RECOVER_ATTEMPT_AT_STORAGE_KEY);
};

/**
 * Compares the baked-in build version with `/version.json` from the network.
 * On mismatch, best-effort clears SW + Cache Storage and reloads. Retries for
 * the same remote version are allowed after {@link VERSION_RECOVER_RETRY_MS}
 * so a failed first recovery cannot permanently stick a client on a deploy.
 *
 * Returns true when a reload was triggered (caller should stop booting).
 */
export const recoverIfDeployedVersionMismatch = async ({
  localVersion = typeof SYSTEM !== 'undefined' ? SYSTEM.version : undefined,
  versionUrl = DEPLOYED_VERSION_URL,
  fetchImpl = fetch,
  clearCaches = clearServiceWorkerAndCaches,
  reload = () => window.location.reload(),
  storage = window.localStorage,
  now = Date.now(),
  retryAfterMs = VERSION_RECOVER_RETRY_MS,
}: RecoverIfDeployedVersionMismatchOptions = {}): Promise<boolean> => {
  if (typeof window === 'undefined' || typeof localVersion !== 'string' || !localVersion) {
    return false;
  }

  try {
    const remoteVersion = await readDeployedVersion(versionUrl, fetchImpl);
    if (!remoteVersion) {
      return false;
    }

    if (remoteVersion === localVersion) {
      clearRecoverAttempt(storage);
      return false;
    }

    const previousAttempt = storage.getItem(VERSION_RECOVER_ATTEMPT_STORAGE_KEY);
    const previousAttemptAt = Number(storage.getItem(VERSION_RECOVER_ATTEMPT_AT_STORAGE_KEY) || '');

    // Same remote deploy: only skip if we tried recently (anti-loop). After the
    // cooldown, try again — the first clear may have lost the race with a
    // still-controlling SW / HTTP cache.
    if (
      previousAttempt === remoteVersion &&
      Number.isFinite(previousAttemptAt) &&
      now - previousAttemptAt < retryAfterMs
    ) {
      return false;
    }

    // Best-effort clear, then always mark + reload for this attempt.
    await clearCaches().catch(() => undefined);
    storage.setItem(VERSION_RECOVER_ATTEMPT_STORAGE_KEY, remoteVersion);
    storage.setItem(VERSION_RECOVER_ATTEMPT_AT_STORAGE_KEY, String(now));
    reload();
    return true;
  } catch (error) {
    // Offline / blocked fetch / parse errors: boot normally.
    // eslint-disable-next-line no-console
    console.warn('[version-recover] check failed', error);
    return false;
  }
};
